import React, { memo, useState, useMemo, useCallback, useEffect } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { getVariableNames } from "../../utils/util";

/* ---------------- helper regexes ---------------- */

/*  identical to MUI's useControlled helper  */
const useControlled = ({ controlled, defaultProp }) => {
  const [state, setState] = useState(defaultProp);
  const isCtrl = controlled !== undefined;
  return [isCtrl ? controlled : state, isCtrl ? () => {} : setState];
};

/* --------------------- component --------------------- */

function TextFieldWithVariableButton({
  value: valueProp,
  param: paramProp,
  onChange,
  variableType = "POSITIONAL",
  varLimitCount = 1,
  charLimit = 512,
}) {
  /* ── controlled / uncontrolled glue ── */
  const [text, setText] = useControlled({
    controlled: valueProp,
    defaultProp: "",
  });
  const [paramText, setParamText] = useControlled({
    controlled: paramProp,
    defaultProp: "",
  });

  const safeLimit = Math.max(10, Math.floor(charLimit));
  const variables = useMemo(
    () => getVariableNames(variableType, text),
    [variableType, text]
  );
  const hasVariable = variables.length > 0;
  const varToken = variables[0]; // only one supported for header

  /* fire parent change immediately */
  const emit = useCallback(
    (nextText, nextParam) => {
      onChange?.(nextText, nextParam);
      setText(nextText);
      setParamText(nextParam ?? "");
    },
    [onChange, setText, setParamText]
  );
  useEffect(() => {
    emit("", "");
  }, [variableType]);
  const addVariable = () => {
    if (hasVariable || text.length >= safeLimit) return;
    const newVar = variableType === "NAMED" ? "{{}}" : `{{1}}`;
    const nextText = text + newVar;
    emit(nextText, paramText);
  };

  const handleTextChange = (e) => {
    const t = e.target.value.slice(0, safeLimit);
    let nextParam = paramText;
    /* if user deletes the token, wipe paramText */
    if (hasVariable && !getVariableNames(variableType, t).length)
      nextParam = "";
    emit(t, nextParam);
  };

  const handleParamChange = (e) => {
    emit(text, e.target.value);
  };

  /* ----------------- derived UI state ----------------- */
  const chars = text.length;
  const charLabel =
    `${chars}/${safeLimit}` +
    (chars === safeLimit
      ? " – limit reached"
      : chars >= safeLimit - 10
      ? " – approaching"
      : "");
  const charColor =
    chars === safeLimit
      ? "error"
      : chars >= safeLimit - 10
      ? "warning"
      : "textSecondary";

  const disableAdd =
    hasVariable || chars >= safeLimit || variables.length >= varLimitCount;

  /* ------------------------ UI ------------------------ */
  return (
    <Box>
      <Box>
        <TextField
          label="Header Text"
          fullWidth
          size="small"
          value={text}
          onChange={handleTextChange}
          helperText={
            <Typography variant="caption" color={charColor}>
              {charLabel}
            </Typography>
          }
        />
      </Box>
      <Box display={"flex"} justifyContent={"end"}>
        <Button
          sx={{ mt: "-18px" }}
          variant="outlined"
          size="small"
          startIcon={<AddOutlinedIcon />}
          disabled={disableAdd}
          onClick={addVariable}
        >
          Variable
        </Button>
      </Box>

      {/* param input (only when token present) */}
      {hasVariable && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
          <code>{`{{${varToken}}}`}</code>
          <TextField
            size="small"
            fullWidth
            label={`Value`}
            value={paramText}
            onChange={handleParamChange}
          />
        </Stack>
      )}
    </Box>
  );
}

export default memo(TextFieldWithVariableButton);
